"use client";

import { Dumbbell, Hand, Search, Trophy } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  icon: Search,
  label: "Step 01",
  name: "Consultation",
  description: "A private, in-depth analysis of your facial anatomy to design a custome balancing plan."
}, {
  icon: Hand,
  label: "Step 02",
  name: "Refinement",
  description: "Precision-led Tox and filler application to restore symmetry and enhance natural proportions."
}, {
  icon: Dumbbell,
  label: "Step 03",
  name: "Prevention",
  description: "Strategic, early-intervention treatments to soften fine lines and maintain youthful skin integrity."
}, {
  icon: Trophy,
  label: "Step 04",
  name: "Radiance",
  description: "Enjoy effortless, refreshed results that celebrate your unique beauty with complete confidence."
}];
export default function Concierge() {
  const heading = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const flow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="18924" id="the-concierge-flow" className="bg-background text-foreground py-24 md:py-32">
      <div className="container mx-auto max-w-[1200px] px-6 md:px-12">
        {}
        <div ref={heading.ref} className="mx-auto max-w-[720px] text-center transition-all duration-700 ease-out" style={{
        opacity: heading.isVisible ? 1 : 0,
        transform: heading.isVisible ? "translateY(0)" : "translateY(16px)"
      }}>
          <p className="font-default text-[13px] font-bold uppercase text-primary" style={{
          letterSpacing: "0.2em"
        }}>
            The Concierge Flow
          </p>

          <h2 className="mt-5 font-default text-4xl md:text-5xl lg:text-[3.25rem] font-medium text-foreground" style={{
          letterSpacing: "-0.015em",
          lineHeight: 1.1
        }}>
            A{" "}
            <span className="relative inline-block italic font-serif">
              refined
              <svg viewBox="0 0 120 12" preserveAspectRatio="none" aria-hidden="true" className="absolute -bottom-1 left-0 w-full h-[10px] overflow-visible">
                <path d="M2,7 Q30,2 60,6 T118,5" fill="none" stroke="hsl(15 45% 75%)" strokeWidth="2" strokeLinecap="round" pathLength={100} strokeDasharray={100} strokeDashoffset={heading.isVisible ? 0 : 100} style={{
                transition: "stroke-dashoffset 900ms cubic-bezier(0.22, 0.61, 0.36, 1) 200ms"
              }} />
              </svg>
            </span>{" "}
            approach to aesthetics.
          </h2>

          <p className="mx-auto mt-6 max-w-[540px] font-default text-base md:text-[1.0625rem] text-muted-foreground leading-relaxed">
            Experience a seamless journey of facial balancing, where clinical precision meets the comfort of a private, concierge-led environment.
          </p>
        </div>

        {}
        <div ref={flow.ref} className="relative mt-20 md:mt-24">
          {}
          <div className="hidden md:block absolute left-0 right-0 top-8 px-[100px] pointer-events-none">
            <div className="h-[1.5px] w-full bg-border origin-left" style={{
            transform: flow.isVisible ? "scaleX(1)" : "scaleX(0)",
            transition: "transform 1400ms cubic-bezier(0.22, 0.61, 0.36, 1) 200ms"
          }} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-6 relative">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <div data-index={i} key={step.label} className="group relative flex md:flex-col items-start md:items-center md:text-center gap-6 md:gap-0" style={{
              opacity: flow.isVisible ? 1 : 0,
              transform: flow.isVisible ? "translateY(0)" : "translateY(16px)",
              transition: `opacity 700ms cubic-bezier(0.22, 0.61, 0.36, 1) ${300 + i * 150}ms, transform 700ms cubic-bezier(0.22, 0.61, 0.36, 1) ${300 + i * 150}ms`
            }}>
                  {}
                  {i < steps.length - 1 && <div className="md:hidden absolute left-8 top-16 bottom-[-3rem] w-[1.5px] bg-border origin-top" style={{
                transform: flow.isVisible ? "scaleY(1)" : "scaleY(0)",
                transition: `transform 600ms cubic-bezier(0.22, 0.61, 0.36, 1) ${400 + i * 150}ms`
              }} />}

                  {}
                  <div className="relative z-10 shrink-0">
                    <div className="w-16 h-16 rounded-lg bg-card border-[1.5px] border-border flex items-center justify-center transition-all duration-300 ease-out group-hover:border-primary group-hover:scale-105">
                      <Icon className="w-7 h-7 text-foreground transition-colors duration-300 ease-out group-hover:text-primary" strokeWidth={1.5} />
                    </div>
                  </div>

                  {}
                  <div className="md:mt-7 flex-1">
                    <p className="font-default text-[13px] font-bold uppercase text-primary" style={{
                  letterSpacing: "0.2em"
                }}>
                      {step.label}
                    </p>
                    <h3 className="mt-2 font-default text-[22px] text-foreground" style={{
                  fontWeight: 500,
                  letterSpacing: "-0.01em",
                  lineHeight: 1.2
                }}>
                      {step.name}
                    </h3>
                    <p className="mt-3 font-default text-[15px] text-muted-foreground leading-relaxed max-w-[220px] md:mx-auto">
                      {step.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}